import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import DescriptionModeSelect from '@cohort/merchants/apps/common/DescriptionModeSelect';
import StepContentInput from '@cohort/merchants/apps/common/StepContentInput';
import StepTitleInput from '@cohort/merchants/apps/common/StepTitleInput';
import type {Defaults} from '@cohort/merchants/apps/common/triggers/utils';
import {
  defaultMedia,
  getDescriptionMode,
  parseDefaults,
} from '@cohort/merchants/apps/common/triggers/utils';
import StravaActivitySelectPicker from '@cohort/merchants/apps/strava/triggerIntegratons/ActivitySelectPicker';
import StravaMetricSelectPicker from '@cohort/merchants/apps/strava/triggerIntegratons/beActive/MetricSelectPicker';
import Input from '@cohort/merchants/components/form/input/Input';
import LanguageSelectorInput from '@cohort/merchants/components/form/LanguageSelectorInput';
import Separator from '@cohort/merchants/components/Separator';
import TriggerIntegrationDateRangeInput from '@cohort/merchants/components/trigger-integration/TriggerIntegrationDateRangeInput';
import type {StravaBeActiveTriggerStruct} from '@cohort/shared/apps/strava/triggers/beActive';
import type {Language} from '@cohort/shared/schema/common';
import {toKilometers, toMeters, toMinutes, toSeconds} from '@cohort/shared/utils/convert';
import {Fragment, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

const getDefaults = (definedLanguages: Language[]): Defaults => {
  const defaults = {
    title: {
      en: `Achieve a goal over a period`,
      fr: `Atteindre un objectif sur une période`,
      es: `Alcanzar un objetivo en un período`,
      de: `Erreichen Sie ein Ziel über einen Zeitraum`,
    },
    description: {},
  };
  return parseDefaults(defaults, definedLanguages);
};

const StravaBeActiveTriggerIntegrationConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps<StravaBeActiveTriggerStruct>
> = props => {
  const init = useRef(false);
  const {
    formReturn: {register, control, reset, watch, setValue},
    step,
    trigger,
  } = props;
  const {t} = useTranslation('app-strava', {
    keyPrefix: 'triggerIntegrations.be-active.configComponent',
  });

  const [definedLanguages] = watch(['definedLanguages']);

  useEffect(() => {
    if (init.current) {
      return;
    }
    const defaults = getDefaults(definedLanguages);

    const hasActivityGroups = trigger?.triggerIntegrationConfig.activityGroups?.length > 0;
    const displayedValue = match(trigger?.triggerIntegrationConfig.metric)
      .with('distance', () => toKilometers(trigger?.triggerIntegrationConfig.value))
      .with('duration', () => toMinutes(trigger?.triggerIntegrationConfig.value))
      .otherwise(() => trigger?.triggerIntegrationConfig.value);

    // Set up default date range: today 00:00 to one week from now 23:59
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    const defaultStartDate = new Date(now);

    const oneWeekFromNow = new Date(now);
    oneWeekFromNow.setDate(now.getDate() + 7);
    oneWeekFromNow.setUTCHours(23, 59, 59, 999);
    const defaultEndDate = new Date(oneWeekFromNow);

    reset(formValues => ({
      ...formValues,
      defaultTitle: defaults.title,
      defaultDescription: defaults.description,
      descriptionMode: getDescriptionMode(definedLanguages, defaults, step),
      title: step?.title ?? defaults.title,
      description: step?.description ?? defaults.description,
      icon: step?.icon !== undefined ? step.icon : '🏃',
      media: step?.media ?? defaultMedia,
      trigger: {
        id: trigger?.id ?? null,
        connectionId: trigger?.connectionId ?? null,
        triggerIntegrationId: 'strava.be-active',
        triggerIntegrationConfig: {
          metric: trigger?.triggerIntegrationConfig.metric ?? null,
          value: trigger?.triggerIntegrationConfig.value ?? null,
          displayedValue,
          activityGroups: trigger?.triggerIntegrationConfig.activityGroups ?? null,
          hasActivityTypes: hasActivityGroups,
          startDate: trigger?.triggerIntegrationConfig.startDate
            ? new Date(trigger.triggerIntegrationConfig.startDate)
            : defaultStartDate,
          endDate: trigger?.triggerIntegrationConfig.endDate
            ? new Date(trigger.triggerIntegrationConfig.endDate)
            : defaultEndDate,
          startDateTimestamp:
            trigger?.triggerIntegrationConfig.startDateTimestamp ?? defaultStartDate.getTime(),
          endDateTimestamp:
            trigger?.triggerIntegrationConfig.endDateTimestamp ?? defaultEndDate.getTime(),
        },
      },
      statementEnabled: false,
    }));

    init.current = true;
  }, [definedLanguages, reset, step, trigger]);

  const metric = watch('trigger.triggerIntegrationConfig.metric');

  const valueLabel = match(metric)
    .with('distance', () => t('labelActivityDistance'))
    .with('duration', () => t('labelActivityDuration'))
    .with('elevation', () => t('labelActivityElevation'))
    .otherwise(() => t('labelActivityValue'));

  const valueDescription = match(metric)
    .with('distance', () => t('descriptionInKilometers'))
    .with('duration', () => t('descriptionInMinutes'))
    .with('elevation', () => t('descriptionInMeters'))
    .otherwise(() => '');

  const valueConvertionMethod = match(metric)
    .with('distance', () => toMeters)
    .with('duration', () => toSeconds)
    .with('elevation', () => null)
    .otherwise(() => null);

  if (!init.current) {
    return null;
  }

  return (
    <Fragment>
      <div>
        <div className="flex flex-col gap-4">
          <TriggerIntegrationDateRangeInput
            {...props}
            startDateName="trigger.triggerIntegrationConfig.startDate"
            endDateName="trigger.triggerIntegrationConfig.endDate"
            startDateTimestampName="trigger.triggerIntegrationConfig.startDateTimestamp"
            endDateTimestampName="trigger.triggerIntegrationConfig.endDateTimestamp"
          />

          <StravaMetricSelectPicker {...props} />

          {metric !== null && (
            <Input
              name="trigger.triggerIntegrationConfig.displayedValue"
              label={valueLabel}
              description={valueDescription}
              register={register}
              control={control}
              type="number"
              min={0}
              onChange={e => {
                setValue('trigger.triggerIntegrationConfig.displayedValue', Number(e.target.value));
                if (valueConvertionMethod !== null) {
                  setValue(
                    'trigger.triggerIntegrationConfig.value',
                    valueConvertionMethod(Number(e.target.value))
                  );
                } else {
                  setValue('trigger.triggerIntegrationConfig.value', Number(e.target.value));
                }
              }}
            />
          )}

          <StravaActivitySelectPicker
            {...props}
            fieldName="trigger.triggerIntegrationConfig.activityGroups"
            hasFieldName="trigger.triggerIntegrationConfig.hasActivityTypes"
          />
        </div>
      </div>
      <DescriptionModeSelect />
      <LanguageSelectorInput />
      <StepTitleInput />
      <Separator />
      <StepContentInput />
    </Fragment>
  );
};

export default StravaBeActiveTriggerIntegrationConfigComponent;
